import { Phone, Search, AlertCircle, CheckCircle, DollarSign, CreditCard } from "lucide-react";
import { useEffect, useRef } from "react";
import Loader from "../Loader";

export default function CustomerSearchStep({ 
  step, 
  formData, 
  customer, 
  customerLoading, 
  customerError, 
  loading,
  onFormChange, 
  onSubmit,
  onAmountSubmit
}) {
  const isCompleted = step > 1;
  const lastMobileRef = useRef("");

  // Auto-search when mobile number reaches 10 digits
  useEffect(() => {
    const mobile = formData.mobile;
    
    // Only search if:
    // 1. We're on step 1
    // 2. Mobile is exactly 10 digits
    // 3. Not currently loading
    // 4. No customer found yet
    // 5. Mobile number has changed from last search
    if (
      step === 1 && 
      mobile.length === 10 && 
      !customerLoading && 
      !customer &&
      mobile !== lastMobileRef.current
    ) {
      lastMobileRef.current = mobile;
      const syntheticEvent = {
        preventDefault: () => {}
      };
      onSubmit(syntheticEvent);
    }
    
    // Reset last mobile if mobile is cleared or changed significantly
    if (mobile.length < 10) {
      lastMobileRef.current = "";
    }
  }, [formData.mobile, step, onSubmit, customerLoading, customer]);
  
  return (
    <div className="theme-card rounded-lg p-4 md:p-6">
      <div className="flex items-center space-x-3 mb-4">
        <div className={`p-2 rounded-lg ${isCompleted ? 'bg-green-100' : 'bg-blue-100'}`}>
          {isCompleted ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <Phone className="h-5 w-5 text-blue-600" />
          )}
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {isCompleted ? 'Transaction Details' : 'New Transaction'}
          </h2>
          <p className="text-sm text-gray-600">
            {isCompleted 
              ? 'Transaction initiated successfully' 
              : customer 
                ? 'Customer found! Enter the transaction amount'
                : 'Enter customer\'s mobile number to search'
            }
          </p>
        </div>
        {isCompleted && (
          <div className="ml-auto">
            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
              Completed
            </span>
          </div>
        )}
      </div>

      {step === 1 ? (
        <div className="space-y-6">
          {/* Mobile Number Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Mobile Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="tel"
                value={formData.mobile}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                  if (value.length <= 10) {
                    onFormChange({ ...formData, mobile: value });
                  }
                }}
                onKeyPress={(e) => {
                  // Only allow digits
                  if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                    e.preventDefault();
                  }
                }}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                placeholder="Enter 10-digit mobile number"
                maxLength="10"
                pattern="[0-9]{10}"
                inputMode="numeric"
                required
              />
            </div>
            {formData.mobile.length > 0 && formData.mobile.length < 10 && (
              <div className="text-red-500 text-xs mt-1">
                Mobile number must be exactly 10 digits
              </div>
            )}
            {formData.mobile.length === 10 && customerLoading && (
              <div className="text-blue-500 text-xs mt-1 flex items-center space-x-1">
                <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <span>Searching for customer...</span>
              </div>
            )}
          </div>

          {/* Customer Error */}
          {customerError && (
            <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg border border-red-200">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm">{customerError}</span>
            </div>
          )}

          {/* Customer Found - Show Details */}
          {customer && (
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-center space-x-2 mb-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-green-800 font-medium">Customer Found</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-green-700">Mobile Number</p>
                  <p className="font-medium text-green-900">{formData.mobile}</p>
                </div>
                <div>
                  <p className="text-sm text-green-700">Customer Name</p>
                  <p className="font-medium text-green-900">{customer.name}</p>
                </div>
                {/* {customer.email && (
                  <div>
                    <p className="text-sm text-green-700">Email</p>
                    <p className="font-medium text-green-900 break-all">{customer.email}</p>
                  </div>
                )} */}
              </div>
            </div>
          )}

          {/* Amount Input - Only show when customer is found */}
          {customer && (
            <form onSubmit={onAmountSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transaction Amount (₹)
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.billableAmount}
                    onChange={(e) => onFormChange({ ...formData, billableAmount: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder="Enter amount"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !formData.billableAmount}
                className="w-full theme-button-gradient py-3 flex items-center justify-center space-x-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5" />
                    <span>Proceed to Review</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-600">Mobile Number</p>
              <p className="font-medium text-gray-900">{formData.mobile}</p>
            </div>
            {customer && (
              <>
                <div>
                  <p className="text-sm text-gray-600">Customer Name</p>
                  <p className="font-medium text-gray-900">{customer.name}</p>
                </div>
                {/* Email section removed */}
              </>
            )}
          </div>
          <div className="border-t pt-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Transaction Amount:</span>
              <span className="font-medium text-gray-900">₹{formData.billableAmount}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}